import { useState, useRef } from 'react'
import { Button, Group, Menu, Modal, Text, List, ScrollArea } from '@mantine/core'
import { IconFileSpreadsheet, IconDownload, IconUpload, IconChevronDown } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { notifications } from '@mantine/notifications'
import api from '../../api/client'

interface ImportExportButtonsProps {
  /** resource key ตรงกับที่ backend registry รู้จัก เช่น 'departments', 'categories', 'brands' ฯลฯ */
  resource: string
  /** เรียกหลัง import สำเร็จ เพื่อ refresh รายการในหน้า */
  onImported?: () => void
  /** true = ซ่อนตัวเลือก Import (ใช้กับหน้าที่มีปุ่ม Import ของตัวเองอยู่แล้ว เช่น locations/brands/vendors) */
  exportOnly?: boolean
}

/**
 * ปุ่ม Import / Export / ดาวน์โหลด Template สำหรับ Configuration resource ใดๆ
 * ใช้ generic endpoint เดียวกันหมด (/configuration/:resource/{import,export,template})
 * แปะที่ไหนก็ได้แค่ระบุ resource — ไม่ต้องเขียน import/export ใหม่ทุกหน้า
 */
export default function ImportExportButtons({ resource, onImported, exportOnly }: ImportExportButtonsProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const downloadBlob = async (url: string, filename: string) => {
    try {
      const res = await api.get(url, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl; a.download = filename; a.click()
      URL.revokeObjectURL(blobUrl)
    } catch {
      notifications.show({ color: 'red', message: t('configImportExport.downloadFailed', 'Download failed') })
    }
  }

  const handleDownloadTemplate = () => downloadBlob(`/configuration/${resource}/template`, `${resource}-template.xlsx`)
  const handleExport = () => downloadBlob(`/configuration/${resource}/export`, `${resource}-export.xlsx`)

  const handleFileSelected = (file: File | null) => {
    if (!file) return
    setPendingFile(file)
    setResult(null)
    setImportModalOpen(true)
  }

  const handleImport = async () => {
    if (!pendingFile) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', pendingFile)
      const res = await api.post(`/configuration/${resource}/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setResult(res.data)
      if (res.data.imported > 0) onImported?.()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('configImportExport.importFailed', 'Import failed') })
    } finally {
      setUploading(false)
    }
  }

  const closeModal = () => {
    setImportModalOpen(false)
    setPendingFile(null)
    setResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  if (exportOnly) {
    return (
      <Button size="sm" variant="outline" color="gray" leftSection={<IconDownload size={14} />} onClick={handleExport}>
        {t('configImportExport.export', 'Export current data')}
      </Button>
    )
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
      />
      <Menu shadow="md" width={220} position="bottom-end">
        <Menu.Target>
          <Button size="sm" variant="outline" color="gray" leftSection={<IconFileSpreadsheet size={14} />} rightSection={<IconChevronDown size={12} />}>
            {t('configImportExport.button', 'Import / Export')}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconUpload size={14} />} onClick={() => fileInputRef.current?.click()}>
            {t('configImportExport.import', 'Import from file')}
          </Menu.Item>
          <Menu.Item leftSection={<IconDownload size={14} />} onClick={handleExport}>
            {t('configImportExport.export', 'Export current data')}
          </Menu.Item>
          <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} onClick={handleDownloadTemplate}>
            {t('configImportExport.template', 'Download template')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal opened={importModalOpen} onClose={closeModal} size="md" radius="lg"
        title={<Text fw={600}>{t('configImportExport.importTitle', 'Import from file')}</Text>}>
        <Text size="sm" c="dimmed" mb="md">{pendingFile?.name}</Text>

        {!result ? (
          <Group justify="flex-end">
            <Button variant="default" onClick={closeModal}>{t('common.cancel', 'Cancel')}</Button>
            <Button loading={uploading} onClick={handleImport}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)' }}>
              {t('configImportExport.import', 'Import from file')}
            </Button>
          </Group>
        ) : (
          <>
            <Group gap="lg" mb="sm">
              <Text size="sm"><b style={{ color: '#0d9488' }}>{result.imported}</b> {t('configImportExport.imported', 'imported')}</Text>
              <Text size="sm"><b style={{ color: '#f59e0b' }}>{result.skipped}</b> {t('configImportExport.skipped', 'skipped')}</Text>
            </Group>
            {result.errors.length > 0 && (
              <ScrollArea h={160} mb="sm">
                <List size="xs" spacing={2}>
                  {result.errors.map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                </List>
              </ScrollArea>
            )}
            <Group justify="flex-end">
              <Button onClick={closeModal}
                style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)' }}>
                {t('common.close', 'Close')}
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  )
}
